<template>
	<button
		type="button"
		:class="
			bemm('', [
				color,
				size,
				variant,
				active ? 'active' : '',
				disabled ? 'disabled' : '',
				loading ? 'loading' : '',
			])
		"
		:style="`--pill-color: var(--color-${color})`"
		:disabled="disabled || loading"
		@click="onClick"
	>
		<Icon v-if="icon && !loading" :name="icon" :class="bemm('icon')" />
		<span v-if="loading" :class="bemm('loader')"></span>
		<span :class="bemm('label')">
			<slot></slot>
		</span>
		<button
			v-if="removable && !disabled && !loading"
			type="button"
			:class="bemm('remove')"
			@click.stop="$emit('remove')"
		>
			<Icon :name="removeIcon" :class="bemm('remove-icon')" />
		</button>
	</button>
</template>

<script lang="ts" setup>
import { PropType } from 'vue';
import { useBemm } from 'bemm';
import { Color, Size } from '@arritech/shared/types';
import { Icons } from '../Icon';
import { Icon, type IconType } from '../Icon';
import type { PillVariant } from './Pill.model';

defineOptions({
	name: 'Pill',
});

const props = defineProps({
	color: {
		type: String as PropType<Color>,
		default: Color.PRIMARY,
	},
	icon: {
		type: String as PropType<IconType | string>,
		default: null,
	},
	size: {
		type: String as PropType<Size>,
		default: Size.MEDIUM,
	},
	variant: {
		type: String as PropType<PillVariant>,
		default: 'filled',
	},
	active: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	removable: {
		type: Boolean,
		default: false,
	},
	removeIcon: {
		type: String as PropType<IconType | string>,
		default: Icons.CROSS_SMALL,
	},
	loading: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['click', 'remove']);

const bemm = useBemm('pill', {
	return: 'string',
	includeBaseClass: true,
});

function onClick(e: MouseEvent) {
	if (!props.disabled && !props.loading) {
		emit('click', e);
	}
}
</script>

<style lang="scss">
.pill {
	--int-pill-background-color: var(
		--pill-background-color,
		color-mix(in srgb, var(--pill-color), var(--color-background) 85%)
	);
	--int-pill-text-color: var(
		--pill-text-color,
		color-mix(in srgb, var(--pill-color), var(--color-foreground) 50%)
	);
	--int-pill-border-color: var(
		--pill-border-color,
		color-mix(in srgb, var(--pill-color), var(--color-background) 70%)
	);

	display: inline-flex;
	align-items: center;
	gap: var(--space-s);
	padding: var(--space-xs) var(--space);
	border-radius: var(--border-radius-xxl);
	border: 1.5px solid transparent;
	background-color: var(--int-pill-background-color);
	color: var(--int-pill-text-color);
	font-family: inherit;
	font-weight: var(--font-weight-bold);
	font-size: var(--font-size-s);
	line-height: 1.4;
	cursor: pointer;
	white-space: nowrap;
	transition:
		background-color 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease,
		opacity 0.2s ease;

	&:hover:not(:disabled) {
		border-color: var(--int-pill-border-color);
	}

	&:focus-visible {
		outline: 2px solid var(--pill-color);
		outline-offset: 2px;
	}

	// ── Sizes ──
	&--xsmall {
		font-size: var(--font-size-xs);
		padding: calc(var(--space-xs) * 0.5) var(--space-xs);
		gap: var(--space-xs);
	}
	&--small {
		font-size: var(--font-size-xs);
		padding: var(--space-xs) var(--space-s);
	}
	&--medium {
		font-size: var(--font-size-s);
		padding: var(--space-xs) var(--space);
	}
	&--large {
		font-size: var(--font-size-m);
		padding: var(--space-s) var(--space);
	}
	&--xlarge {
		font-size: var(--font-size-l);
		padding: var(--space-s) var(--space-l);
	}

	// ── Variants ──
	&--filled {
		background-color: var(--int-pill-background-color);
		color: var(--int-pill-text-color);
	}

	&--outlined {
		background-color: transparent;
		border-color: var(--int-pill-border-color);
		color: var(--int-pill-text-color);
	}

	&--ghost {
		background-color: transparent;
		border-color: transparent;
		color: var(--int-pill-text-color);

		&:hover:not(:disabled) {
			background-color: var(--int-pill-background-color);
		}
	}

	// ── States ──
	&--active {
		border-color: var(--pill-color);
		color: var(--pill-color);

		--int-pill-background-color: var(
			--pill-background-color,
			color-mix(in srgb, var(--pill-color), var(--color-background) 80%)
		);
	}

	&--disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	&--loading {
		cursor: wait;
	}

	// ── Elements ──
	&__icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	&__label {
		display: inline-flex;
		align-items: center;
	}

	&__remove {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: none;
		border: none;
		padding: 0;
		margin: 0;
		cursor: pointer;
		color: inherit;
		opacity: 0.6;
		transition: opacity 0.15s ease;

		&:hover {
			opacity: 1;
		}
	}

	&__remove-icon {
		display: inline-flex;
	}

	&__loader {
		display: inline-block;
		width: 1em;
		height: 1em;
		border: 2px solid currentColor;
		border-right-color: transparent;
		border-radius: 50%;
		animation: pill-spin 0.6s linear infinite;
	}
}

@keyframes pill-spin {
	to {
		transform: rotate(360deg);
	}
}
</style>
